/**
 * Platform-scope governance panels — mounted in PlatformConsole.
 *
 * Exports: MfaPolicyPanel, RetentionPanel, SSOPanel, ResidencyPanel
 *
 * All panels are Platform Root only; PlatformConsole already guards access.
 */
import React, { useEffect, useState } from 'react';
import GlassSelect from './GlassSelect';
import { formatBackendDetail } from '../lib/apiErrors';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── MFA platform policy ─────────────────────────────────────────── */
export function MfaPolicyPanel() {
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    const r = await fetch(`${API}/api/auth/mfa/policy`);
    if (r.ok) setPolicy((await r.json()).policy);
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/auth/mfa/policy`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error(formatBackendDetail((await r.json().catch(() => ({}))).detail) || 'Save failed');
      setPolicy((await r.json()).policy);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  if (!policy) return null;
  const toggle = (key) => save({ [key]: !policy[key] });

  return (
    <div className="panel" style={{ marginBottom: 12 }} data-testid="mfa-policy-panel">
      <div className="panel-header">MFA policy</div>
      <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {err && <div className="auth-error">{err}</div>}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={policy.require_for_platform_root} disabled={saving}
                 onChange={() => toggle('require_for_platform_root')}
                 data-testid="mfa-policy-root-toggle" />
          Require MFA for Platform Support Admin accounts
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={policy.require_for_platform_admins} disabled={saving}
                 onChange={() => toggle('require_for_platform_admins')}
                 data-testid="mfa-policy-admins-toggle" />
          Require MFA for tenant Owners &amp; Admins
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={policy.require_for_all_users} disabled={saving}
                 onChange={() => toggle('require_for_all_users')}
                 data-testid="mfa-policy-all-toggle" />
          Require MFA for all users
        </label>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
          Changes apply at next sign-in. Users are force-enrolled inline on the login page.
        </div>
      </div>
    </div>
  );
}

/* ─── Retention ───────────────────────────────────────────────────── */
export function RetentionPanel() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sweep, setSweep] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    const r = await fetch(`${API}/api/platform/retention/policy`);
    if (r.ok) setData(await r.json());
  };
  useEffect(() => { load(); }, []);

  const savePolicy = async (collection, days) => {
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/platform/retention/policy`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ collection, days: parseInt(days, 10) }),
      });
      if (!r.ok) throw new Error(formatBackendDetail((await r.json().catch(() => ({}))).detail) || 'Save failed');
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const runSweep = async (dryRun) => {
    setErr(null);
    const r = await fetch(`${API}/api/platform/retention/sweep?dry_run=${dryRun}`, { method: 'POST' });
    if (!r.ok) { setErr('Sweep failed'); return; }
    setSweep(await r.json());
  };

  if (!data) return null;

  return (
    <div className="panel" style={{ marginBottom: 12 }} data-testid="retention-panel">
      <div className="panel-header">Data retention</div>
      <div className="panel-body">
        {err && <div className="auth-error">{err}</div>}
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
          Records older than the policy window are permanently deleted by a nightly sweep.
          Audit logs are never touched.
        </div>
        <div className="data-table-wrap">
          <table className="data-table" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>Collection</th><th>Retention</th><th>Default</th><th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {data.collections.map((c) => (
                <tr key={c.collection} data-testid={`retention-row-${c.collection}`}>
                  <td style={{ fontWeight: 600 }}>
                    {c.label}
                    <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontFamily: "'IBM Plex Mono', monospace", fontWeight: 400 }}>
                      {c.collection}
                    </div>
                  </td>
                  <td>
                    <GlassSelect value={c.retention_days} disabled={saving}
                            onChange={(e) => savePolicy(c.collection, e.target.value)}
                            data-testid={`retention-select-${c.collection}`}
                            style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4 }}>
                      {data.allowed_days.map((d) => (
                        <option key={d} value={d}>
                          {d} day{d > 1 ? 's' : ''}
                        </option>
                      ))}
                    </GlassSelect>
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    {c.default_days}d{c.is_default ? ' ✓' : ''}
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    {c.description}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={() => runSweep(true)}
                  data-testid="retention-dry-run-btn">Run dry-run</button>
          <button className="btn btn-outline btn-sm" onClick={() => runSweep(false)}
                  data-testid="retention-sweep-btn" style={{ color: 'var(--danger)' }}>
            Sweep now (permanent)
          </button>
        </div>
        {sweep && (
          <pre data-testid="retention-sweep-output" style={{ fontSize: 11, background: 'var(--bg)', padding: 8, borderRadius: 4, marginTop: 8 }}>
            {JSON.stringify(sweep, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

/* ─── Residency ───────────────────────────────────────────────────── */
export function ResidencyPanel() {
  const [r, setR] = useState(null);
  useEffect(() => {
    (async () => {
      const res = await fetch(`${API}/api/platform/info/residency`);
      if (res.ok) setR(await res.json());
    })();
  }, []);
  if (!r) return null;
  return (
    <div className="panel" style={{ marginBottom: 12 }} data-testid="residency-panel">
      <div className="panel-header">Declared data residency — not verified</div>
      <div className="panel-body" style={{ fontSize: 13, lineHeight: 1.5 }}>
        <p role="status" data-testid="residency-declaration-notice">Operator declarations only. This panel does not verify hosting, backups, processing locations, encryption or legal compliance.</p>
        <div><strong>Declared primary region:</strong> {r.primary_region || 'Unknown / unconfigured'}</div>
        <div><strong>Declared backup region:</strong> {r.single_region === true ? `${r.backup_region} (declared same region; topology not verified)` : r.backup_region || 'Unknown / unconfigured'}</div>
        <div><strong>Declared hosting provider:</strong> {r.hosting_provider || 'Unknown / unconfigured'}</div>
        <div><strong>Declared compliance:</strong> {Array.isArray(r.compliance) && r.compliance.length ? r.compliance.join(', ') : 'Unknown / unconfigured'}</div>
        <div><strong>Declared encryption at rest:</strong> {r.encryption_at_rest || 'Unknown / unconfigured'}</div>
        <div><strong>Declared encryption in transit:</strong> {r.encryption_in_transit || 'Unknown / unconfigured'}</div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8 }}>
          {r.disclaimer}
        </div>
      </div>
    </div>
  );
}

/* ─── SSO / SAML ──────────────────────────────────────────────────── */
export function SSOPanel() {
  const [configs, setConfigs] = useState([]);
  const [err, setErr] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [form, setForm] = useState({
    name: '', entity_id: '', idp_entity_id: '', idp_sso_url: '',
    idp_slo_url: '', idp_x509_cert: '', acs_url: '', default_role: 'user',
  });
  const [metaXml, setMetaXml] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await fetch(`${API}/api/sso/configs`);
    if (r.ok) setConfigs((await r.json()).configs);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/sso/configs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(formatBackendDetail((await r.json().catch(() => ({}))).detail) || 'Save failed');
      setShowNew(false);
      setForm({ name: '', entity_id: '', idp_entity_id: '', idp_sso_url: '',
                idp_slo_url: '', idp_x509_cert: '', acs_url: '', default_role: 'user' });
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const parseMeta = async () => {
    setErr(null);
    try {
      const r = await fetch(`${API}/api/sso/metadata/parse`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ xml: metaXml }),
      });
      if (!r.ok) throw new Error(formatBackendDetail((await r.json().catch(() => ({}))).detail) || 'Parse failed');
      const parsed = await r.json();
      setForm((f) => ({ ...f, ...parsed }));
    } catch (e) { setErr(e.message); }
  };

  const toggle = async (cfg) => {
    setSaving(true);
    try {
      await fetch(`${API}/api/sso/configs/${cfg.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !cfg.enabled }),
      });
      await load();
    } finally { setSaving(false); }
  };

  const remove = (cfg) => setPendingDelete(cfg);
  const confirmRemove = async () => {
    if (!pendingDelete) return;
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/sso/configs/${pendingDelete.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(formatBackendDetail((await r.json().catch(() => ({}))).detail) || 'Delete failed');
      setPendingDelete(null);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="panel" style={{ marginBottom: 12 }} data-testid="sso-panel">
      <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>SSO / SAML</span>
        <button className="btn btn-outline btn-sm" onClick={() => setShowNew(!showNew)}
                data-testid="sso-add-btn">{showNew ? 'Cancel' : '+ Add IdP'}</button>
      </div>
      <div className="panel-body">
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
          <strong>Foundation only.</strong> Config CRUD, metadata import and audit logging are live.
          The SP-initiated handshake (<code>/api/sso/login</code>, <code>/api/sso/acs</code>) is scaffolded
          and will be enabled in the next sprint after IdP verification.
        </div>
        {err && <div className="auth-error">{err}</div>}
        {pendingDelete && (
          <div className="panel" style={{ marginBottom: 8, borderColor: 'var(--danger)' }}
               data-testid="sso-remove-confirm">
            <div className="panel-body" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13 }}>
                Delete SSO config <strong>{pendingDelete.name}</strong>? This cannot be undone.
              </span>
              <button className="btn btn-primary btn-sm" style={{ background: 'var(--danger)' }}
                      onClick={confirmRemove} disabled={saving}
                      data-testid="sso-remove-confirm-btn">
                {saving ? 'Removing…' : 'Yes, remove'}
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => setPendingDelete(null)}
                      data-testid="sso-remove-cancel-btn">Cancel</button>
            </div>
          </div>
        )}
        {configs.length === 0 && !showNew && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No SSO providers configured.</div>
        )}
        {configs.map((c) => (
          <div key={c.id} style={{ borderBottom: '1px solid var(--border)', padding: '8px 0', fontSize: 12 }}
               data-testid={`sso-row-${c.id}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {c.name}
                  <span style={{
                    marginLeft: 8, fontSize: 10, padding: '2px 6px', borderRadius: 10,
                    background: c.enabled ? '#dcfce7' : '#f3f4f6',
                    color: c.enabled ? '#166534' : '#4b5563',
                  }}>{c.enabled ? 'ENABLED' : 'DISABLED'}</span>
                </div>
                <div style={{ color: 'var(--text-secondary)', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10 }}>
                  IdP: {c.idp_entity_id}
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 10 }}>
                  Cert: {c.idp_x509_cert_fingerprint || '—'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button className="btn btn-outline btn-sm" onClick={() => toggle(c)} disabled={saving}
                        data-testid={`sso-toggle-${c.id}`}>
                  {c.enabled ? 'Disable' : 'Enable'}
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => remove(c)}
                        data-testid={`sso-remove-${c.id}`} style={{ color: 'var(--danger)' }}>
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))}

        {showNew && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <h4 style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' }}>
              New SAML provider
            </h4>
            <div style={{ marginBottom: 8 }}>
              <textarea placeholder="Paste IdP metadata XML (optional) then click Parse"
                        value={metaXml} onChange={(e) => setMetaXml(e.target.value)}
                        data-testid="sso-metadata-xml"
                        style={{ width: '100%', minHeight: 60, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, padding: 6, border: '1px solid var(--border)', borderRadius: 4 }} />
              <button className="btn btn-outline btn-sm" onClick={parseMeta} disabled={!metaXml}
                      data-testid="sso-parse-btn" style={{ marginTop: 4 }}>Parse metadata</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                ['name', 'Name (e.g. Acme Okta)'],
                ['entity_id', 'SP entity ID (our side)'],
                ['idp_entity_id', 'IdP entity ID'],
                ['idp_sso_url', 'IdP SSO URL'],
                ['acs_url', 'ACS URL (our callback)'],
                ['idp_slo_url', 'IdP SLO URL (optional)'],
              ].map(([k, label]) => (
                <label key={k} style={{ fontSize: 10, textTransform: 'uppercase' }}>
                  {label}
                  <input type="text" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                         data-testid={`sso-field-${k}`}
                         style={{ width: '100%', padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4, fontFamily: k.includes('url') || k.includes('entity') ? "'IBM Plex Mono', monospace" : 'inherit', fontSize: 12 }} />
                </label>
              ))}
              <label style={{ gridColumn: 'span 2', fontSize: 10, textTransform: 'uppercase' }}>
                IdP X.509 certificate (PEM)
                <textarea value={form.idp_x509_cert}
                          onChange={(e) => setForm({ ...form, idp_x509_cert: e.target.value })}
                          data-testid="sso-field-idp_x509_cert"
                          style={{ width: '100%', minHeight: 80, padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10 }} />
              </label>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving}
                      data-testid="sso-submit-btn">{saving ? 'Saving…' : 'Save'}</button>
              <button className="btn btn-outline btn-sm" onClick={() => setShowNew(false)}
                      data-testid="sso-cancel-btn">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
